import { useCallback, useMemo, useReducer } from 'react';

import {
	type OnCodeVerificationRedirection,
	type OnRegistrationRedirection,
	type OnUserDetailsRedirection,
	type WelcomeProps,
	type WelcomeReducer,
	type WelcomeReducerAction
} from '../models';

import { CodeVerification } from './code-verificationv2/CodeVerification';
import { Register } from './registration';
import { UserDetails } from './user-detailsv2/UserDetails';

export const Welcome = ({ variant }: WelcomeProps) => {
	const [{ formattedValues, stage, verificationCode }, dispatch] = useReducer(
		(state: WelcomeReducer, action: WelcomeReducerAction) => {
			switch (action.stage) {
				case 'user-details': {
					return {
						formattedValues: state.formattedValues,
						stage: action.stage,
						verificationCode: undefined
					};
				}
				case 'code-verification': {
					return {
						formattedValues: action.data.formattedValues,
						stage: action.stage,
						verificationCode: undefined
					};
				}
				case 'registration': {
					return {
						formattedValues: state.formattedValues,
						stage: action.stage,
						verificationCode: action.data.verificationCode
					};
				}
				default:
					return state;
			}
		},
		{
			formattedValues: undefined,
			stage: 'user-details',
			verificationCode: undefined
		}
	);

	const onUserDetailsRedirection = useCallback<OnUserDetailsRedirection>(() => {
		dispatch({
			stage: 'user-details'
		});
	}, [dispatch]);

	const onRegistrationRedirection = useCallback<OnRegistrationRedirection>(
		({ verificationCode }) => {
			dispatch({
				data: {
					verificationCode
				},
				stage: 'registration'
			});
		},
		[dispatch]
	);

	const onCodeVerificationRedirection = useCallback<OnCodeVerificationRedirection>(
		formattedValues => {
			dispatch({
				data: { formattedValues },
				stage: 'code-verification'
			});
		},
		[dispatch]
	);

	return useMemo(() => {
		if (stage === 'code-verification' && formattedValues) {
			return (
				<CodeVerification
					formattedValues={formattedValues}
					variant={variant}
					onRegistrationRedirection={variant === 'register' ? onRegistrationRedirection : undefined}
					onUserDetailsRedirection={onUserDetailsRedirection}
				/>
			);
		}

		if (stage === 'registration' && formattedValues && verificationCode) {
			const { medium, type } = formattedValues;

			return type === 'email' ? (
				<Register email={medium.email} type={type} validationCode={verificationCode} />
			) : (
				<Register
					countryCode={medium.countryCode}
					mobileNumber={medium.mobileNumber}
					type={type}
					validationCode={verificationCode}
				/>
			);
		}

		return (
			<UserDetails
				formattedValues={formattedValues}
				variant={variant}
				onStageChange={onCodeVerificationRedirection}
			/>
		);
	}, [
		formattedValues,
		onCodeVerificationRedirection,
		onRegistrationRedirection,
		onUserDetailsRedirection,
		stage,
		variant,
		verificationCode
	]);
};
